import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FILTERS } from "@/components/go-live/GoLiveSheets";
import { Loader2 } from "lucide-react";

const GIFT_KEYS = ["rose", "fire", "kiss", "diamond", "bottle", "cake", "crown", "rocket", "lambo", "yacht"];
const EXTENSIONS = ["Chatbot Mod", "Voice Effects", "Confetti Cannon", "Auto Captions", "Guest Queue"];

const Row = ({ label, children }) => (
  <div className="mb-4">
    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{label}</div>
    {children}
  </div>
);

/**
 * Slice 1 — Live Tools editor. Everything the old pre-Live wall offered,
 * now editable MID-STREAM from Creator Control Center. Saves via
 * PUT /live/{id}/settings; viewers pick changes up on the stream-doc poll.
 * Paid Live is intentionally absent (immutable after go-live).
 */
export default function LiveToolsPanel({ open, onOpenChange, stream }) {
  const [beauty, setBeauty] = useState(false);
  const [filter, setFilter] = useState("none");
  const [goalCoins, setGoalCoins] = useState(0);
  const [wheel, setWheel] = useState(["", "", "", ""]);
  const [wheelName, setWheelName] = useState("");
  const [wheelPrice, setWheelPrice] = useState(25);
  const [wheelEnabled, setWheelEnabled] = useState(true);
  const [prizes, setPrizes] = useState([]);
  const [customGifts, setCustomGifts] = useState([]);
  const [extensions, setExtensions] = useState([]);
  const [split, setSplit] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !stream) return;
    setBeauty(!!stream.beauty);
    setFilter(stream.filter || "none");
    setGoalCoins(Number(stream.goal_coins) || 0);
    const w = Array.isArray(stream.wheel) ? stream.wheel.slice(0, 8) : [];
    setWheel(w.length ? w : ["", "", "", ""]);
    setWheelName(stream.wheel_name || "");
    setWheelPrice(Number(stream.wheel_price) || 25);
    setWheelEnabled(stream.wheel_enabled !== false);
    api.get(`/live/${stream.id}/wheel/prizes`).then(({ data }) => setPrizes(Array.isArray(data) ? data : [])).catch(() => setPrizes([]));
    setCustomGifts(Array.isArray(stream.custom_gifts) ? stream.custom_gifts : []);
    setExtensions(Array.isArray(stream.extensions) ? stream.extensions : []);
    setSplit(!!stream.split_enabled);
  }, [open, stream]);

  const save = async () => {
    if (busy || !stream?.id) return;
    setBusy(true);
    try {
      await api.put(`/live/${stream.id}/settings`, {
        beauty,
        filter,
        goal_coins: goalCoins || 0,
        wheel: wheel.filter((s) => (s || "").trim()),
        wheel_name: wheelName,
        wheel_price: wheelPrice,
        wheel_enabled: wheelEnabled,
        custom_gifts: customGifts,
        extensions,
        split_enabled: split,
      });
      toast.success("Live tools updated");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save settings.");
    } finally {
      setBusy(false);
    }
  };

  const markPrizeDone = async (id) => {
    try {
      await api.post(`/creator/prizes/${id}/done`, {});
      setPrizes((cur) => cur.map((p) => (p.id === id ? { ...p, status: "done" } : p)));
      toast.success("Winner notified");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update prize."); }
  };

  const Toggle = ({ on, onClick, label, testId }) => (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`px-3 py-2 rounded-xl border text-sm transition ${
        on ? "bg-yellow-400/10 border-yellow-400 text-yellow-300" : "bg-white/5 border-white/10 text-white"
      }`}
    >
      {label}
    </button>
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        data-testid="live-tools-panel"
        className="z-[120] bg-zinc-950/95 backdrop-blur-xl border-t border-white/10 text-white rounded-t-2xl max-h-[80vh] overflow-y-auto"
      >
        <SheetHeader>
          <SheetTitle className="text-white text-sm uppercase tracking-widest">Live Tools</SheetTitle>
        </SheetHeader>

        <div className="mt-3">
          <Row label="Appearance">
            <div className="flex flex-wrap gap-2">
              <Toggle on={beauty} onClick={() => setBeauty((b) => !b)} label="✨ Beauty" testId="lt-beauty" />
              {FILTERS.map((f) => (
                <Toggle
                  key={f.key}
                  on={filter === f.key}
                  onClick={() => setFilter(f.key)}
                  label={f.label}
                  testId={`lt-filter-${f.key}`}
                />
              ))}
            </div>
          </Row>

          <Row label="Stream goal (coins)">
            <input
              type="number"
              min="0"
              data-testid="lt-goal-input"
              value={goalCoins || ""}
              onChange={(e) => setGoalCoins(Math.max(0, parseInt(e.target.value || "0", 10)))}
              placeholder="0 = no goal"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-yellow-400/50"
            />
          </Row>

          <Row label="Prize wheel (2+ segments to enable)">
            <div className="grid grid-cols-2 gap-2 mb-2">
              <input data-testid="lt-wheel-name" value={wheelName} onChange={(e) => setWheelName(e.target.value.slice(0, 40))} placeholder="Wheel name (Prize wheel)"
                className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-yellow-400/50" />
              <input data-testid="lt-wheel-price" type="number" min="1" max="100000" value={wheelPrice}
                onChange={(e) => setWheelPrice(Math.max(1, Math.min(100000, parseInt(e.target.value || "1", 10))))} placeholder="Coins per spin"
                className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-yellow-400/50" />
            </div>
            <div className="flex items-center gap-2 mb-2">
              <Toggle on={wheelEnabled} onClick={() => setWheelEnabled((v) => !v)} label={wheelEnabled ? "Wheel ON" : "Wheel OFF"} testId="lt-wheel-enabled" />
              <span className="text-[11px] text-white/50">{wheelPrice} coins per spin · edits apply to future spins</span>
              {wheel.length < 8 && (
                <button type="button" data-testid="lt-wheel-add" onClick={() => setWheel((cur) => [...cur, ""])} className="ml-auto text-[11px] text-yellow-300">+ segment</button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {wheel.map((seg, i) => (
                <input
                  key={i}
                  data-testid={`lt-wheel-${i}`}
                  value={seg}
                  onChange={(e) =>
                    setWheel((cur) => cur.map((s, j) => (j === i ? e.target.value : s)))
                  }
                  placeholder={`Segment ${i + 1}`}
                  className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-yellow-400/50"
                />
              ))}
            </div>
            {prizes.length > 0 && (
              <div className="mt-3 space-y-1" data-testid="lt-wheel-prizes">
                <div className="text-[11px] uppercase tracking-widest text-white/50">Prizes to fulfil</div>
                {prizes.map((p) => (
                  <div key={p.id} className="flex items-center gap-2 text-sm" data-testid={`lt-prize-${p.id}`}>
                    <span className={p.status === "done" ? "text-white/40 line-through" : "text-white"}>{p.sender_display_name || "Panda Member"} · {p.segment} · {p.coins} coins</span>
                    {p.status === "pending" ? (
                      <button type="button" data-testid={`lt-prize-done-${p.id}`} onClick={() => markPrizeDone(p.id)} className="ml-auto text-[11px] px-2 py-0.5 rounded-full bg-yellow-400 text-black font-semibold">Done</button>
                    ) : <span className="ml-auto text-[11px] text-emerald-300">done</span>}
                  </div>
                ))}
              </div>
            )}
          </Row>

          <Row label="Custom gifts">
            <div className="flex flex-wrap gap-2">
              {GIFT_KEYS.map((g) => (
                <Toggle
                  key={g}
                  on={customGifts.includes(g)}
                  onClick={() =>
                    setCustomGifts((cur) =>
                      cur.includes(g) ? cur.filter((x) => x !== g) : [...cur, g]
                    )
                  }
                  label={g}
                  testId={`lt-gift-${g}`}
                />
              ))}
            </div>
          </Row>

          <Row label="Extensions">
            <div className="flex flex-wrap gap-2">
              {EXTENSIONS.map((ext) => (
                <Toggle
                  key={ext}
                  on={extensions.includes(ext)}
                  onClick={() =>
                    setExtensions((cur) =>
                      cur.includes(ext) ? cur.filter((x) => x !== ext) : [...cur, ext]
                    )
                  }
                  label={ext}
                  testId={`lt-ext-${ext.replace(/\s+/g, "-").toLowerCase()}`}
                />
              ))}
            </div>
          </Row>

          <Row label="Layout">
            <Toggle on={split} onClick={() => setSplit((s) => !s)} label="Split layout" testId="lt-split" />
          </Row>

          <button
            type="button"
            data-testid="lt-save"
            onClick={save}
            disabled={busy}
            className="w-full mt-1 mb-2 bg-yellow-400 hover:bg-yellow-300 disabled:opacity-60 text-black font-bold py-3 rounded-full text-sm flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : "Save changes"}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
